import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles, categories } from '@/mocks/articles';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const [activeSlug, setActiveSlug] = useState(slug || 'safety-tips');

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  const activeCategory = categories.find((c) => c.slug === activeSlug);
  const filteredArticles = articles.filter((a) => a.categorySlug === activeSlug);

  const itemsPageLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: activeCategory ? `${activeCategory.name} - 污视频安全资讯` : '分类页面',
    description: activeCategory?.description || '',
    url: `https://example.com/category/${activeSlug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: filteredArticles.map((article, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `https://example.com/article/${article.slug}`,
      })),
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(itemsPageLd)}</script>
      <Navbar />
      <main className="pt-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: activeCategory?.name || '分类' },
          ]}
        />

        <section className="w-full px-4 md:px-6 py-6 md:py-10 bg-background-50">
          <h1 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-2">
            {activeCategory?.name}
          </h1>
          <p className="text-sm text-foreground-600 mb-6">{activeCategory?.description}</p>

          <div className="flex flex-wrap gap-2 mb-8">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                  activeSlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-600 border border-background-200/70'
                }`}
              >
                <i className={`${cat.icon} mr-1.5`}></i>
                {cat.name}
              </Link>
            ))}
          </div>

          {filteredArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {filteredArticles.map((article) => (
                <Link
                  key={article.id}
                  to={`/article/${article.slug}`}
                  className="group flex flex-col bg-background-100 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
                >
                  <div className="p-4 md:p-5 flex flex-col flex-1">
                    <div className="flex items-center gap-2 mb-3">
                      {article.isHot && (
                        <span className="px-2 py-0.5 bg-primary-500 text-background-50 text-xs font-medium rounded-full whitespace-nowrap">热门</span>
                      )}
                      {article.isNew && (
                        <span className="px-2 py-0.5 bg-accent-500 text-background-50 text-xs font-medium rounded-full whitespace-nowrap">新发布</span>
                      )}
                    </div>
                    <h2 className="text-base md:text-lg font-heading font-semibold text-foreground-900 mb-2 group-hover:text-primary-600 transition-colors leading-snug">
                      {article.title}
                    </h2>
                    <p className="text-sm text-foreground-600 leading-relaxed line-clamp-3 mb-4 flex-1">
                      {article.summary}
                    </p>
                    <div className="flex items-center justify-between text-xs text-foreground-500">
                      <span>{article.publishDate}</span>
                      <span className="flex items-center gap-1">
                        阅读全文 <i className="ri-arrow-right-line"></i>
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-foreground-500 text-sm">该分类暂无文章</p>
            </div>
          )}
        </section>
      </main>
      <Footer />
    </>
  );
}